import React, { useState } from 'react';
import {
  useUsageAnalytics,
  useRevenueAnalytics,
  useSuccessRateAnalytics,
} from '@/hooks/api/useAdmin';
import { CardSkeleton, ErrorMessage } from '@/components/ui';

interface AnalyticsDashboardProps {
  className?: string;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ className = '' }) => {
  const [revenuePeriod, setRevenuePeriod] = useState<'day' | 'week' | 'month'>('month');
  const [stateFilter, setStateFilter] = useState<string>('');

  const {
    data: usageData,
    isLoading: isLoadingUsage,
    error: usageError,
  } = useUsageAnalytics();

  const {
    data: revenueData,
    isLoading: isLoadingRevenue,
    error: revenueError,
  } = useRevenueAnalytics(undefined, undefined, revenuePeriod);

  const {
    data: successData,
    isLoading: isLoadingSuccess,
    error: successError,
  } = useSuccessRateAnalytics(stateFilter || undefined, 30);

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* Usage Analytics Section */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Usage Analytics</h2>

        {isLoadingUsage ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : usageError ? (
          <ErrorMessage message="Failed to load usage analytics" />
        ) : usageData ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{formatNumber(usageData.total_users)}</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm font-medium text-gray-500 mb-1">Active Users (30d)</p>
              <p className="text-3xl font-bold text-green-600">
                {formatNumber(usageData.active_users_30d)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {((usageData.active_users_30d / usageData.total_users) * 100).toFixed(1)}% of total
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm font-medium text-gray-500 mb-1">Total Searches</p>
              <p className="text-3xl font-bold text-blue-600">
                {formatNumber(usageData.total_searches)}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm font-medium text-gray-500 mb-1">Searches (7d)</p>
              <p className="text-3xl font-bold text-indigo-600">
                {formatNumber(usageData.searches_last_7d)}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm font-medium text-gray-500 mb-1">Searches (30d)</p>
              <p className="text-3xl font-bold text-purple-600">
                {formatNumber(usageData.searches_last_30d)}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm font-medium text-gray-500 mb-1">Avg Searches per User</p>
              <p className="text-3xl font-bold text-gray-900">
                {usageData.avg_searches_per_user.toFixed(1)}
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-sm font-medium text-gray-500 mb-1">New Users (30d)</p>
              <p className="text-3xl font-bold text-green-600">
                {formatNumber(usageData.new_users_30d)}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {/* Revenue Analytics Section */}
      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Revenue Analytics</h2>

          <div className="flex gap-2">
            <button
              onClick={() => setRevenuePeriod('day')}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                revenuePeriod === 'day'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setRevenuePeriod('week')}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                revenuePeriod === 'week'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setRevenuePeriod('month')}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                revenuePeriod === 'month'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Monthly
            </button>
          </div>
        </div>

        {isLoadingRevenue ? (
          <div className="space-y-4">
            <CardSkeleton />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          </div>
        ) : revenueError ? (
          <ErrorMessage message="Failed to load revenue analytics" />
        ) : revenueData ? (
          <div className="space-y-4">
            {/* Total Revenue Card */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-md p-6 text-white">
              <p className="text-sm font-medium mb-1 opacity-90">Total Revenue</p>
              <p className="text-4xl font-bold">{formatCurrency(revenueData.total_revenue)}</p>
              <p className="text-sm mt-2 opacity-80">Period: {revenueData.period}</p>
            </div>

            {/* Revenue Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 mb-1">Average Transaction</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatCurrency(revenueData.average_transaction)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 mb-1">Top-up Revenue</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(revenueData.topup_revenue)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {((revenueData.topup_revenue / revenueData.total_revenue) * 100).toFixed(1)}% of
                  total
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 mb-1">Search Revenue</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(revenueData.search_revenue)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {((revenueData.search_revenue / revenueData.total_revenue) * 100).toFixed(1)}% of
                  total
                </p>
              </div>
            </div>

            {/* Revenue Chart */}
            {revenueData.revenue_by_period.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Revenue by {revenuePeriod === 'day' ? 'Day' : revenuePeriod === 'week' ? 'Week' : 'Month'}
                </h3>
                <div className="space-y-3">
                  {revenueData.revenue_by_period.map((item) => {
                    const maxRevenue = Math.max(
                      ...revenueData.revenue_by_period.map((p) => p.revenue)
                    );
                    const percentage = (item.revenue / maxRevenue) * 100;

                    return (
                      <div key={item.period}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-700">{item.period}</span>
                          <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(item.revenue)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.transaction_count} transactions
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Success Rate Analytics Section */}
      <div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Success Rate Analytics</h2>

          <div className="w-full sm:w-64">
            <label htmlFor="state-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by State
            </label>
            <select
              id="state-filter"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All States</option>
              <option value="CA">California</option>
              <option value="TX">Texas</option>
              <option value="FL">Florida</option>
              <option value="NY">New York</option>
              <option value="IL">Illinois</option>
            </select>
          </div>
        </div>

        {isLoadingSuccess ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : successError ? (
          <ErrorMessage message="Failed to load success rate analytics" />
        ) : successData ? (
          <div className="space-y-4">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 mb-1">Success Rate</p>
                <p className="text-3xl font-bold text-green-600">
                  {formatPercentage(successData.success_rate)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatNumber(successData.successful)} / {formatNumber(successData.total_extractions)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Extractions</p>
                <p className="text-3xl font-bold text-gray-900">
                  {formatNumber(successData.total_extractions)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 mb-1">Avg Confidence</p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatPercentage(successData.avg_confidence_score)}
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <p className="text-sm font-medium text-gray-500 mb-1">Avg Extraction Time</p>
                <p className="text-3xl font-bold text-purple-600">
                  {successData.avg_extraction_time_seconds.toFixed(1)}s
                </p>
              </div>
            </div>

            {/* Failure Reasons Chart */}
            {Object.keys(successData.failure_reasons).length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Failure Reasons</h3>
                <div className="space-y-3">
                  {Object.entries(successData.failure_reasons)
                    .sort(([, a], [, b]) => b - a)
                    .map(([reason, count]) => {
                      const percentage = (count / successData.failed) * 100;

                      return (
                        <div key={reason}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-700">{reason}</span>
                            <span className="text-sm font-bold text-gray-900">
                              {count} ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-red-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Extractions by State */}
            {Object.keys(successData.extractions_by_state).length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Extractions by State</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {Object.entries(successData.extractions_by_state)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 10)
                    .map(([state, count]) => (
                      <div key={state} className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{state}</p>
                        <p className="text-sm text-gray-600 mt-1">{formatNumber(count)}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
